import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import categoryService from "../services/categoryService";
import fileUpload from "../utils/fileUpload";
import fileDelete from "../utils/fileDelete";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "./AdminDashboard.css";

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [name, setName] = useState("");
  const [image, setImage] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  useEffect(() => {
    const apiKey = localStorage.getItem("apiKey");
    const token = localStorage.getItem("token");
    if (!token || !apiKey) {
      toast.error("দয়া করে প্রথমে লগইন করুন এবং API কী সেট করুন", {
        position: "top-right",
      });
      navigate("/login");
      return;
    }
    loadCategories();
  }, [navigate]);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const response = await categoryService.getCategories();
      setCategories(
        Array.isArray(response.data.data) ? response.data.data : []
      );
    } catch (error) {
      toast.error(
        "ক্যাটাগরি লোড করতে সমস্যা: " +
          (error.response?.data?.error || error.message),
        {
          position: "top-right",
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let imageUrl = editingCategory ? editingCategory.image : "";

      if (image) {
        imageUrl = await fileUpload(image);
      }
      const categoryData = { name, image: imageUrl };

      if (editingCategory) {
        const response = await categoryService.updateCategory(
          editingCategory._id,
          categoryData
        );

        toast.success("ক্যাটাগরি সফলভাবে আপডেট হয়েছে!", {
          position: "top-right",
        });
      } else {
        const response = await categoryService.createCategory(categoryData);

        toast.success("ক্যাটাগরি সফলভাবে তৈরি হয়েছে!", {
          position: "top-right",
        });
      }
      resetForm();
      loadCategories();
    } catch (error) {
      console.error("Error in handleCreate:", error);
      toast.error(
        `${editingCategory ? "আপডেট" : "তৈরি"} ত্রুটি: ` +
          (error.response?.data?.error || error.message),
        { position: "top-right" }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setName(category.name);
    setImage(null);
    setEditingCategory(category);
  };

  const handleDelete = async (id) => {
    if (window.confirm("আপনি কি এই ক্যাটাগরিটি মুছে ফেলতে চান?")) {
      setLoading(true);
      try {
        // ক্যাটাগরি খুঁজে বের করুন
        const category = categories.find((cat) => cat._id === id);
        if (!category) {
          throw new Error("ক্যাটাগরি পাওয়া যায়নি।");
        }

        // ইমেজ ফাইল ডিলিট করুন (যদি থাকে)
        if (category.image) {
          await fileDelete(category.image);
        }

        // ক্যাটাগরি ডাটাবেস থেকে ডিলিট করুন
        await categoryService.deleteCategory(id);

        toast.success("ক্যাটাগরি সফলভাবে মুছে ফেলা হয়েছে!", {
          position: "top-right",
        });
        loadCategories();
      } catch (error) {
        toast.error(
          "মুছে ফেলার ত্রুটি: " +
            (error.response?.data?.error || error.message),
          {
            position: "top-right",
          }
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const resetForm = () => {
    setName("");
    setImage(null);
    setEditingCategory(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="admin-dashboard">
      <Header />
      <ToastContainer />
      <div className="container py-5">
        <div className="row g-4">
          <div className="col-lg-12">
            <div className="card shadow-lg p-4 gradient-bg">
              <h2 className="text-center mb-4 text-white">
                {editingCategory ? "ক্যাটাগরি আপডেট" : "নতুন ক্যাটাগরি তৈরি"}
              </h2>
              <form onSubmit={handleCreate}>
                <div className="mb-3">
                  <label htmlFor="name" className="form-label text-white">
                    ক্যাটাগরির নাম
                  </label>
                  <input
                    type="text"
                    className="form-control rounded-pill"
                    id="name"
                    placeholder="ক্যাটাগরির নাম লিখুন"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="image" className="form-label text-white">
                    ইমেজ নির্বাচন করুন
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    id="image"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={(e) => setImage(e.target.files[0])}
                    disabled={loading}
                  />
                  {editingCategory && editingCategory.image && (
                    <div className="mt-2">
                      <p className="text-white">বর্তমান ইমেজ:</p>
                      <img
                        src={`${process.env.REACT_APP_API_URL}/${editingCategory.image}`}
                        alt="Current category"
                        style={{
                          width: "100px",
                          height: "100px",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <button
                    type="submit"
                    className="btn btn-register rounded-pill px-4"
                    disabled={loading}
                  >
                    {editingCategory ? <FaEdit /> : <FaPlus />}
                    {editingCategory ? " আপডেট" : " তৈরি"}
                  </button>
                  {editingCategory && (
                    <button
                      type="button"
                      className="btn btn-cancel rounded-pill px-4"
                      onClick={resetForm}
                      disabled={loading}
                    >
                      বাতিল
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
          <div className="col-lg-12 mt-4">
            <div className="card shadow-lg p-4">
              <h2 className="text-center mb-4 gradient-text">
                ক্যাটাগরি তালিকা
              </h2>
              {loading && <div className="alert alert-info">লোড হচ্ছে...</div>}
              {categories.length === 0 && !loading && (
                <div className="alert alert-warning">
                  কোনো ক্যাটাগরি পাওয়া যায়নি।
                </div>
              )}
              {categories.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-hover table-bordered">
                    <thead className="gradient-bg text-white">
                      <tr>
                        <th>ইমেজ</th>
                        <th>নাম</th>
                        <th>অ্যাকশন</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categories.map((category) => (
                        <tr key={category._id} className="table-row-hover">
                          <td>
                            {category.image ? (
                              <img
                                src={`${process.env.REACT_APP_API_URL}/${category.image}`}
                                alt={category.name}
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              "কোনো ইমেজ নেই"
                            )}
                          </td>
                          <td>{category.name}</td>
                          <td>
                            <button
                              className="btn btn-edit me-2"
                              onClick={() => handleEdit(category)}
                              disabled={loading}
                            >
                              <FaEdit /> এডিট
                            </button>
                            <button
                              className="btn btn-delete"
                              onClick={() => handleDelete(category._id)}
                              disabled={loading}
                            >
                              <FaTrash /> মুছুন
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoriesPage;
